import {useMemo} from 'react';
import {formatCurrency, formatPercent} from 'lib/currency';
import {useTranslation} from 'lib/i18n';
import {getBorrowerBirthDate} from 'lib/borrower';
import {usePrintContext} from 'contexts/print-context';
import {useBooleanToString} from 'hooks/currency';
import {parentheses} from 'lib/utils';
import {isPaymentBreakdownValueNegative, formatPaymentBreakdownValue} from 'lib/product';

export function useHybridOverallResults(props = {}) {
  const {
    product: {
      ApiRoute,
      calculation: {
        BorrowerProfile: {
          Age,
          DateOfBirth,
          Liens,
          ValueOfProperty,
          ExistingMortgage: {TaxesAndInsuranceReserve} = {},
        } = {},
        PrincipalLimit,
        ClosingCost,
        FundAccess,
        OverrideClosingCosts,
        InitialLoanBalance,
        HybridResult: {NewMonthlyPayment} = {},
        CashAtClose,
        RemainingPrincipalLimit,
        TotalInitialRate,
      } = {},
    } = {},
  } = props;
  const {t, override, cashLabel} = useTranslation();

  const {booleanToString} = useBooleanToString();

  const {printing} = usePrintContext();

  // This is used on the Product PDF Printout page
  const overallResults = useMemo(
    () => [
      {
        label: t('illustratorTranslation:illustration.overall results.home value'),
        value: formatCurrency(ValueOfProperty),
        order: 1,
        id: 'home-value',
      },
      {
        label: t('illustratorTranslation:illustration.overall results.principal limit'),
        tooltip: t('illustratorTranslation:illustration.overall results.principal limit tooltip'),
        value: formatCurrency(PrincipalLimit),
        order: 2,
        id: 'principal-limit',
        options: [
          {
            label: t('illustratorTranslation:illustration.overall results.mortgage balance'),
            value: parentheses(formatCurrency(Liens)),
            useColorOptions: {value: 'red'},
            order: 3,
            id: 'mortgage-balance',
          },
          {
            label: t('illustratorTranslation:illustration.overall results.closing cost'),
            value: parentheses(formatCurrency(ClosingCost)),
            useColorOptions: {value: 'red'},
            order: 4,
            id: 'closing-cost',
          },
          {
            label: t('illustratorTranslation:illustration.overall results.t&i reserves'),
            tooltip: t('translation:justText.based on 6 month reserve'),
            value: parentheses(formatCurrency(TaxesAndInsuranceReserve)),
            useColorOptions: {value: 'red'},
            order: 5,
            id: 't-i-reserves',
          },
        ],
      },
      {
        label: cashLabel('illustratorTranslation:illustration.overall results.cash at close', {
          cash: CashAtClose,
        }),
        value: formatPaymentBreakdownValue(CashAtClose),
        useColorOptions: {value: isPaymentBreakdownValueNegative(CashAtClose) ? 'red' : undefined},
        order: 6,
        id: 'cash-at-close',
      },
      {
        label: t('illustratorTranslation:illustration.overall results.remaining principal limit'),
        tooltip: t('illustratorTranslation:illustration.overall results.remaining principal limit tooltip'),
        value: formatCurrency(RemainingPrincipalLimit),
        order: 7,
        id: 'remaining-principal-limit',
      },
      {
        label: t('illustratorTranslation:illustration.overall results.initial loan balance'),
        value: formatCurrency(InitialLoanBalance),
        order: 8,
        id: 'initial-loan-balance',
      },
      {
        label: override('illustratorTranslation:illustration.overall results.future payment amount', {
          product: {ApiRoute},
        }),
        value: formatCurrency(NewMonthlyPayment),
        order: 9,
        id: 'future-payment-amount',
      },
    ],
    [
      t,
      ValueOfProperty,
      PrincipalLimit,
      Liens,
      ClosingCost,
      TaxesAndInsuranceReserve,
      CashAtClose,
      RemainingPrincipalLimit,
      InitialLoanBalance,
      override,
      cashLabel,
      ApiRoute,
      NewMonthlyPayment,
    ],
  );

  // This is used on the Product PDF Printout page
  const overallResultsSecond = useMemo(() => {
    const data = [
      {
        label: t('illustratorTranslation:illustration.overall results.date of birth'),
        value: getBorrowerBirthDate(DateOfBirth),
        order: 1,
        id: 'date-of-birth',
      },
      {
        label: t('translation:justText.estimated age'),
        tooltip: t('translation:justText.estimated age tooltip'),
        value: Age,
        order: 2,
        id: 'estimated-age',
      },
      {
        label: override('illustratorTranslation:illustration.overall results.Rate', {product: {ApiRoute}}),
        value: formatPercent(TotalInitialRate),
        order: 3,
        id: 'rate',
      },
      {
        label: override('illustratorTranslation:illustration.overall results.payment term', {product: {ApiRoute}}),
        value: t('illustratorTranslation:illustration.overall results.equity avail years'),
        order: 4,
        id: 'payment-term',
      },
    ];

    if (printing) {
      data.push(
        {
          label: t('illustratorTranslation:illustration.overall results.fund access'),
          value: FundAccess,
          order: 5,
          id: 'fund-access',
        },
        {
          label: t('illustratorTranslation:illustration.overall results.override closing costs'),
          value: booleanToString(OverrideClosingCosts),
          order: 6,
          id: 'override-closing-costs',
        },
      );
    }

    return data;
  }, [
    t,
    DateOfBirth,
    Age,
    override,
    ApiRoute,
    TotalInitialRate,
    printing,
    FundAccess,
    booleanToString,
    OverrideClosingCosts,
  ]);

  return {
    overallResults,
    overallResultsSecond,
  };
}
